/* =====================================================================
 * Tenant bootstrap (Phase 11-4i slimmed).
 *
 * This module has a single responsibility now: resolve the active
 * tenant manifest ONCE at import time and publish it to
 * `@auw/tenant`'s module-scope registry.  `main.tsx` performs a
 * side-effect import of this file at the very top of its import list,
 * ensuring `setActiveTenantManifest()` runs BEFORE any shared-package
 * module body that might call `activeTenantKey()` / `activeTenantId()`
 * / `activeTenantAppName()` during its own evaluation.
 *
 * Historically this file also exposed `tenantKey()` and
 * `tenantNamespacedKey()` helpers that consumers imported via
 * `@/tenant/storage`.  P11-4i moves every call-site to
 * `@auw/tenant`'s tenant-aware equivalents (`activeTenantKey`,
 * `activeTenantNamespacedKey`).  The file is kept for its bootstrap
 * side-effect alone — do NOT re-introduce helpers here, add them to
 * `@auw/tenant` and consume them from there so enterprise hosts stay
 * in sync.
 *
 * Why module-scope is safe:
 *   Vite bundles `resolveTenantManifest()` synchronously — it only
 *   reads `import.meta.env.VITE_TENANT_ID` / a small sync manifest
 *   table. No `window`/DOM access, no async work, no circular import
 *   back into React.
 *
 * Why we export a marker (instead of pure side-effect):
 *   A naked `import './tenant/storage'` from `main.tsx` is a
 *   side-effect-only import. With Vite/Rollup chunking
 *   (`build.rollupOptions.output.manualChunks` splits PdfRenderer /
 *   ExcelRenderer / docAgent into lazy chunks) the bundler is free to
 *   hoist this module into a secondary chunk shared with another
 *   transitive consumer of `@auw/tenant`. When that happens,
 *   `setActiveTenantManifest()` runs at lazy-chunk eval time —
 *   strictly AFTER `@auw/i18n`'s module body has already called
 *   `getActiveTenantManifest()` and crashed the bootstrap with
 *   "Active tenant manifest has not been set". Re-deploying without
 *   this marker reproduces the white-screen / blank dev.askyour.ai
 *   regression observed on 2026-04-29.
 *
 *   Exporting `__TENANT_REGISTRY_BOOTSTRAPPED` and consuming it via a
 *   named import in `main.tsx` ties the module to the entry chunk: a
 *   live binding from the entry forces Rollup to keep the module in
 *   the entry chunk's eval graph, which guarantees the
 *   side-effect runs before any other `main.tsx` import body (i18n,
 *   workspace-utils, …).
 * ===================================================================== */

import { setActiveTenantManifest } from '@auw/tenant';
import { resolveTenantManifest } from './index';

const _activeManifest = resolveTenantManifest();
setActiveTenantManifest(_activeManifest);

/**
 * Returns the tenant id that was registered at this module's
 * top-level evaluation. The exact return value is unimportant — the
 * function exists so `main.tsx` can hold a live binding into this
 * module via a *call expression* the minifier (Terser) cannot prove
 * pure-and-unused. A naked `export const __MARKER__ = true` was
 * inlined-and-dead-coded in the 2026-04-29 build, which let Rollup
 * hoist the entire side-effect module out of the entry chunk and
 * into `feature-preview-pdf` (transitively a PdfRenderer dependency
 * via `@auw/tenant`). The result was that
 * `setActiveTenantManifest()` only ran when the user lazy-loaded a
 * PDF preview — too late: `@auw/i18n`'s module body had already
 * thrown "Active tenant manifest has not been set" and the SPA
 * showed a blank screen on dev.askyour.ai.
 *
 * Keeping this as a *function* call (not a const) defeats both the
 * tree-shaker and Terser's pure-call optimiser, pinning the module
 * to the entry chunk's eval graph.
 */
export function getBootstrappedTenantId(): string {
  return _activeManifest.id;
}
